import { Avatar, Button, Image, Modal, notification, Tooltip, Upload } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { PATIENT_PROFILE_CLOUDFRONT } from '../../api/constants';
import { track } from '../../tracking';
import PatientPicModal from './PatientPicModal';
import ImgCrop from 'antd-img-crop';
import { randomString } from '../../store/utils';

const TakePatientPic: React.FC<{
    pic?: string | File;
    setPic: (imageFile?: File) => void;
}> = ({ setPic, pic = '' }) => {
    const [openCameraModal, setOpenCameraModal] = useState(false);
    const [iconImage, setIconImage] = useState('');

    const updateProfileImage = () => {
        if (pic?.constructor?.name === 'File') {
            const iconImage = URL.createObjectURL(pic as File);
            setIconImage(iconImage);
        } else {
            setIconImage(pic as string);
        }
    };

    useEffect(() => {
        updateProfileImage();
    }, [pic]);

    const closeCameraModal = () => {
        setOpenCameraModal(false);
    };

    const onClickCameraIcon = () => {
        setOpenCameraModal(true);
    };

    return (
        <div className="relative space-y-16 pr-12">
            <Tooltip placement="left" title={'Pic may take some minutes to reflect in profile.'}>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                    className="absolute cursor-pointer w-16 right-20 top-10"
                >
                    <path d="M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336c-13.3 0-24 10.7-24 24s10.7 24 24 24h80c13.3 0 24-10.7 24-24s-10.7-24-24-24h-8V248c0-13.3-10.7-24-24-24H216c-13.3 0-24 10.7-24 24s10.7 24 24 24h24v64H216zm40-144a32 32 0 1 0 0-64 32 32 0 1 0 0 64z" />
                </svg>
            </Tooltip>

            <div className="space-y-8 flex flex-col items-center">
                <Avatar
                    shape="circle"
                    size={100}
                    icon={<img src={iconImage || 'https://a.eka.care/user-avatar/dummyimage'} />}
                />
                <div className="flex space-x-36 items-center justify-between">
                    <ImgCrop rotationSlider>
                        <Upload
                            id="patient-pic"
                            name="file"
                            method="post"
                            listType="picture"
                            accept="image/png, image/jpeg, image/apng, image/avif, image/gif, image/svg+xml, image/webp"
                            multiple={false}
                            showUploadList={false}
                            customRequest={async (e) => {
                                // this value is 3 MB = 3145728 Bytes
                                if ((e.file as File).size < 3145728) {
                                    setPic(e.file as File);
                                } else {
                                    notification.error({
                                        message:
                                            'File is too large. please upload file lesser than 5MB',
                                        duration: 5,
                                        placement: 'topRight',
                                    });
                                    track('ERROR', {
                                        on: 'PatientPicModal',
                                        what: 'patient picture is too large',
                                    });
                                }
                                return true;
                            }}
                            beforeUpload={(file) => {
                                return file;
                            }}
                        >
                            <Button className="border-none text-14 font-700 text-darwin-primary-500">
                                Upload
                            </Button>
                        </Upload>
                    </ImgCrop>
                    <div
                        onClick={onClickCameraIcon}
                        className="flex text-darwin-primary-500 text-14 font-700 space-x-8 items-center cursor-pointer"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 512 512"
                            className="w-16 fill-darwin-primary-500"
                        >
                            <path d="M199.1 32c-24.1 0-45.5 15.4-53.1 38.3l22.8 7.6-22.8-7.6L137.4 96H64C28.7 96 0 124.7 0 160V416c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V160c0-35.3-28.7-64-64-64H374.6l-8.6-25.7C358.4 47.4 337 32 312.9 32H199.1zm-7.6 53.5c1.1-3.3 4.1-5.5 7.6-5.5H312.9c3.4 0 6.5 2.2 7.6 5.5l14 42.1c3.3 9.8 12.4 16.4 22.8 16.4H448c8.8 0 16 7.2 16 16V416c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16V160c0-8.8 7.2-16 16-16h90.7c10.3 0 19.5-6.6 22.8-16.4l14-42.1zM256 400a112 112 0 1 0 0-224 112 112 0 1 0 0 224zM192 288a64 64 0 1 1 128 0 64 64 0 1 1 -128 0z" />
                        </svg>
                        <span className="whitespace-nowrap">Click</span>
                    </div>
                </div>
            </div>
            {openCameraModal && (
                <PatientPicModal
                    closeCameraModal={closeCameraModal}
                    openCameraModal={openCameraModal}
                    setPic={setPic}
                />
            )}
        </div>
    );
};

export default TakePatientPic;
